import { useState } from 'react'
import Select from '../../../../components/Select'
import CreditCardInput from '../../../../components/CreditCardInput/CreditCardInput'

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
]

export default function PaymentMethodStep ({ savedAch, method, onChange }) {
  const [mode, setMode] = useState(method?.mode || 'card')

  const setModeAndClear = (next) => {
    setMode(next)
    if (next === 'saved' && savedAch) {
      onChange({
        mode: 'saved',
        methodType: 'ach',
        customerVaultId: savedAch.customerVaultId,
        last4: savedAch.last4,
        methodLabel: savedAch.methodLabel
      })
      return
    }
    if (next === 'card') {
      onChange({ mode: 'card', methodType: 'card' })
      return
    }
    onChange({ mode: 'ach', methodType: 'ach' })
  }

  return (
    <div className='flex flex-col gap-4'>
      <div className='rounded-2xl border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-900/20 px-4 py-3'>
        <p className='text-sm font-semibold text-sky-900 dark:text-sky-200'>Credit card is recommended</p>
        <p className='text-sm text-sky-800 dark:text-sky-300 mt-1'>Card payments post immediately. Bank / ACH is still available if you prefer to use an account on file.</p>
      </div>

      <div className='flex flex-wrap gap-2'>
        <ModeButton active={mode === 'card'} recommended onClick={() => setModeAndClear('card')}>Credit card</ModeButton>
        {savedAch && (
          <ModeButton active={mode === 'saved'} onClick={() => setModeAndClear('saved')}>Saved bank ···{savedAch.last4}</ModeButton>
        )}
        <ModeButton active={mode === 'ach'} onClick={() => setModeAndClear('ach')}>New bank account</ModeButton>
      </div>

      {mode === 'card' && (
        <CreditCardInput
          ccnumber={method.ccnumber}
          ccexp={method.ccexp}
          cvv={method.cvv}
          onChange={(fields) => onChange({ ...method, mode: 'card', methodType: 'card', ...fields })}
        />
      )}
      {mode === 'saved' && savedAch && (
        <p className='text-sm text-slate-700 dark:text-slate-200'>We will debit {savedAch.methodLabel || 'your saved bank'} ending in {savedAch.last4}. ACH can take several business days to clear.</p>
      )}
      {mode === 'ach' && (
        <BankFields value={method} onChange={(fields) => onChange({ ...method, mode: 'ach', methodType: 'ach', ...fields })} />
      )}
    </div>
  )
}

function ModeButton ({ active, recommended, onClick, children }) {
  return (
    <button
      type='button'
      onClick={onClick}
      className={`px-4 py-2 rounded-xl text-sm font-semibold border ${active
        ? 'bg-sky-600 text-white border-sky-600'
        : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-gray-200 dark:border-slate-600'}`}
    >
      {children}
      {recommended ? <span className='ml-2 text-[10px] uppercase tracking-wide opacity-90'>Recommended</span> : null}
    </button>
  )
}

function BankFields ({ value = {}, onChange }) {
  const set = (key, val) => onChange({ ...value, [key]: val })
  return (
    <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
      <Input label='Bank name' value={value.bankName} onChange={(v) => set('bankName', v)} />
      <Input label='Account name' value={value.accountName} onChange={(v) => set('accountName', v)} />
      <SelectField label='Account type' value={value.accountType || 'checking'} onChange={(v) => set('accountType', v)} options={[{ value: 'checking', label: 'Checking' }, { value: 'savings', label: 'Savings' }]} />
      <SelectField label='Holder type' value={value.accountHolderType || 'business'} onChange={(v) => set('accountHolderType', v)} options={[{ value: 'business', label: 'Business' }, { value: 'personal', label: 'Personal' }]} />
      <Input label='Routing number' value={value.routingNumber} onChange={(v) => set('routingNumber', v)} />
      <Input label='Account number' value={value.accountNumber} onChange={(v) => set('accountNumber', v)} />
      <Input label='First name' value={value.firstName} onChange={(v) => set('firstName', v)} />
      <Input label='Last name' value={value.lastName} onChange={(v) => set('lastName', v)} />
      <Input className='sm:col-span-2' label='Address' value={value.address1} onChange={(v) => set('address1', v)} />
      <Input label='City' value={value.city} onChange={(v) => set('city', v)} />
      <SelectField label='State' value={value.state || ''} onChange={(v) => set('state', v)} options={US_STATES.map((s) => ({ value: s, label: s }))} />
      <Input label='ZIP' value={value.zip} onChange={(v) => set('zip', v)} />
    </div>
  )
}

function Input ({ label, value, onChange, className = '', autoComplete }) {
  return (
    <label className={`flex flex-col gap-1 ${className}`}>
      <span className='text-xs font-semibold text-slate-500 dark:text-slate-400'>{label}</span>
      <input
        className='rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-white'
        value={value || ''}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  )
}

function SelectField ({ label, value, onChange, options }) {
  return (
    <div className='flex flex-col gap-1'>
      <span className='text-xs font-semibold text-slate-500 dark:text-slate-400'>{label}</span>
      <Select value={value} onChange={onChange} options={options} />
    </div>
  )
}
